import { Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from "react";
import { faBuildingColumns, faBuilding, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuthorizeProviderMutation } from "../../../../api/hooks/useAuthorizeProviderMutation.ts";
import { DataContext } from "../../../../dataProviders/DataProvider.tsx";
import { Card, Modal, Tabs, Flag, ScrollArea, Input, Button, Tooltip } from "../../../../components";
import { getNameFromCountryCode } from "../../../../utils/getLocale.ts";
import { PaymentProvidersResponse } from "../../../../api";
import css from "./AddBankModal.module.css";
import { clsx } from "clsx";
import { DialogProps } from "@radix-ui/react-dialog";
import { useToast } from "../../../../components/Toast/useToast.ts";
import { logError } from "../../../../utils/sentry.ts";
import { sortCountries } from "../../utils/sortCountries.ts";
import { useForm } from "react-hook-form";
import { isValid } from "iban";
import { t, Trans } from "@lingui/macro";
import infoIcon from "../../../../assets/info.svg";
import { ProviderAuthorizeInput } from "../../../../api/types.ts";

interface Props extends Pick<DialogProps, "open" | "onOpenChange"> {
  open?: boolean;
  onOpenChange?: Dispatch<SetStateAction<boolean>>;
  trigger?: ReactNode;
}

type Input = {
  iban: string;
  psuid: string;
};

type StructuredProviders = Record<string, PaymentProvidersResponse["banks"]>;

const PROVIDERS_DEMANDING_IBAN = ["KH_HU", "RAIFFEISEN_HU", "EBH_HU", "CIB_HU", "RAIFFEISEN_RO", "BRD_RO", "ALIOR_PL"];
const PROVIDERS_DEMANDING_PSUID = ["RAIFFEISEN_HU", "EBH_HU", "CIB_HU", "RAIFFEISEN_RO", "BRD_RO", "PKO_PL"];
const PROVIDERS_WITH_SEGMENT_CONTEXT = [
  "ALIOR_PL",
  "BNPPARIBAS_PL",
  "CIB_HU",
  "MILLENIUM_PL",
  "PEKAO_PL",
  "BRD_RO",
  "RAIFFEISEN_RO",
  "SANTANDER_PL",
];

export function AddBankModal({ open, onOpenChange, trigger }: Props) {
  const { paymentProviders } = useContext(DataContext);

  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Input>();

  const [selectedProvider, setSelectedProvider] = useState<string>();
  const [segmentContextType, setSegmentContextType] = useState<ProviderAuthorizeInput["segmentContextType"]>("RETAIL");
  const selectedProviderObject = useMemo(() => {
    return paymentProviders.find((provider) => provider.identification.paymentProvider === selectedProvider);
  }, [paymentProviders, selectedProvider]);
  const shouldDisplayIbanInput =
    PROVIDERS_DEMANDING_IBAN.includes(selectedProvider ?? "") &&
    !(selectedProvider === "BRD_RO" && segmentContextType === "CORPORATE");
  const shouldDisplayPsuidInput = PROVIDERS_DEMANDING_PSUID.includes(selectedProvider ?? "");
  const shouldDisplaySegmentContext = PROVIDERS_WITH_SEGMENT_CONTEXT.includes(selectedProvider ?? "");

  const authorizeMutation = useAuthorizeProviderMutation();

  const infoMap: Record<string, string> = {
    BRD_RO: t`Klientské ID (známé jako YOU BRD login ID) je 8místné číslo používané pro přihlášení do mobilní a internetové aplikace BRD. Toto ID lze vyhledat v mobilní nebo internetové aplikaci výběrem možnosti „Autentificare in Mobile Token“ nebo kontaktováním BRD na čísle +4 021 302 6161.`,
    RAIFFEISEN_RO: t`Klientské ID (známé jako PSU-ID nebo PSU-Corporate-ID) je uživatelské jméno používané k přihlášení do aplikace banky. Uživatelské jméno najdete v aplikaci banky v pravém horním rohu hlavního panelu > SPRÁVCE PROFILU UŽIVATELE > SPRÁVA PROFILU > UŽIVATELSKÁ DATA.`,
  };

  const authorizeBankProvider = async (
    paymentProvider: string,
    segmentContextType: ProviderAuthorizeInput["segmentContextType"],
    iban?: string,
    psuId?: string,
  ) => {
    try {
      const { redirectUrl } = await authorizeMutation.mutateAsync({
        psuId,
        paymentProvider,
        segmentContextType,
        accountIdentifications: iban ? [{ type: "IBAN", value: iban }] : undefined,
        redirectUrl: window.location.protocol + "//" + window.location.host + window.location.pathname,
      });
      window.location.href = redirectUrl;
    } catch (e) {
      showToast({ variant: "attention", title: t`Připojení banky se nezdařilo`, description: t`Zkuste to znovu.` });
    }
  };

  const { structuredProviders, countryKeys } = useMemo(() => {
    const structuredProviders: StructuredProviders = {};
    try {
      for (let i = 0; i < paymentProviders.length; i++) {
        const country = paymentProviders[i].identification.countryCode;
        if (!structuredProviders[country]) structuredProviders[country] = [];
        structuredProviders[country] = [...structuredProviders[country], paymentProviders[i]];
      }
      const countryKeys = Object.keys(structuredProviders);

      const sortedCountries = sortCountries(countryKeys);

      return { structuredProviders, countryKeys: sortedCountries };
    } catch (error) {
      logError(error);
      return { structuredProviders, countryKeys: [] };
    }
  }, [paymentProviders]);

  const [activeCountry, setActiveCountry] = useState(Object.keys(structuredProviders)[0]);

  const hasPsuidInfo = selectedProvider && Object.keys(infoMap).includes(selectedProvider);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      title={t`Výběr banky pro synchronizaci`}
      className={clsx(!selectedProvider && "min-h-[32.1rem]")}
      icon={
        <FontAwesomeIcon
          className={css.icon}
          icon={faBuildingColumns}
          width={"22px"}
          height={"22px"}
          color={"#3518ec"}
        />
      }
    >
      {!selectedProvider && (
        <>
          <ScrollArea orientation={"horizontal"}>
            <div className={clsx(css.boxContainer, "pb-2")}>
              {countryKeys.map((key, index) => (
                <Tabs
                  onClick={() => setActiveCountry(key)}
                  key={key + String(index)}
                  isActive={activeCountry === key}
                  variant={"box"}
                  startIcon={<Flag code={key} />}
                >
                  {getNameFromCountryCode(key)}
                </Tabs>
              ))}
            </div>
          </ScrollArea>
          <ScrollArea orientation={"vertical"} className={"max-h-[350px] mt-6"}>
            <div className={"flex gap-3 flex-wrap"}>
              {structuredProviders[activeCountry].map(({ identification }) => (
                <button
                  key={identification.paymentProvider}
                  title={identification.name}
                  onClick={() => {
                    const paymentProvider = identification.paymentProvider;
                    const needsAdditionalData =
                      PROVIDERS_DEMANDING_IBAN.includes(paymentProvider) || PROVIDERS_DEMANDING_PSUID.includes(paymentProvider);
                    const needsSegmentContext = PROVIDERS_WITH_SEGMENT_CONTEXT.includes(paymentProvider);

                    if (needsAdditionalData || needsSegmentContext) {
                      setSelectedProvider(paymentProvider);
                      setSegmentContextType("RETAIL");
                      return;
                    }

                    authorizeBankProvider(paymentProvider, "RETAIL");
                  }}
                  className={"block"}
                >
                  <Card key={identification.paymentProvider} variant={"light"} size={"sm"}>
                    <img
                      src={identification.logoUrl}
                      alt={identification.name}
                      className={"w-28 h-16 object-contain"}
                    />
                  </Card>
                </button>
              ))}
            </div>
          </ScrollArea>
        </>
      )}
      {selectedProvider && (
        <div>
          {shouldDisplaySegmentContext && (
            <>
              <p className={"mb-6 typo-display-xxs-medium"}>
                <Trans>
                  Vyberte segment pro banku{" "}
                  <span className={"typo-display-xxs-bold"}>{selectedProviderObject?.identification.name}</span>:
                </Trans>
              </p>
              <div className={"flex gap-3 mb-6"}>
                <Tabs
                  variant={"box"}
                  isActive={segmentContextType === "RETAIL"}
                  onClick={() => setSegmentContextType("RETAIL")}
                  startIcon={<FontAwesomeIcon icon={faUser} />}
                >
                  retail
                </Tabs>
                <Tabs
                  variant={"box"}
                  isActive={segmentContextType === "CORPORATE"}
                  onClick={() => setSegmentContextType("CORPORATE")}
                  startIcon={<FontAwesomeIcon icon={faBuilding} />}
                >
                  corporate
                </Tabs>
              </div>
            </>
          )}

          {(shouldDisplayPsuidInput || shouldDisplayIbanInput) && (
            <p className={"mb-6 typo-display-xxs-medium"}>
              <Trans>Pro připojení této banky doplňte ještě následující údaje:</Trans>
            </p>
          )}

          <form
            onSubmit={handleSubmit((data) =>
              authorizeBankProvider(
                selectedProvider,
                shouldDisplaySegmentContext ? segmentContextType : "RETAIL",
                data.iban,
                data.psuid,
              ),
            )}
          >
            <div className={"flex flex-col gap-4 mb-8"}>
              {shouldDisplayPsuidInput && (
                <div className={"flex items-center gap-4"}>
                  <Input
                    label={t`ID klienta`}
                    required={true}
                    containerProps={{ className: "w-full max-w-sm" }}
                    className={"min-w-[16rem] md:min-w-[20rem]"}
                    {...register("psuid", {
                      required: t`Toto pole je povinné`,
                    })}
                    isError={!!errors.psuid?.message}
                    messages={errors.psuid?.message ? [{ variant: "error", text: errors.psuid.message }] : undefined}
                  />
                  {hasPsuidInfo && (
                    <Tooltip
                      side={"right"}
                      trigger={<img src={infoIcon} alt={"info"} className={"mt-3.5 w-5 h-5"} />}
                      text={infoMap[selectedProvider]}
                    />
                  )}
                </div>
              )}
              {shouldDisplayIbanInput && (
                <Input
                  label={"IBAN"}
                  required={true}
                  containerProps={{ className: "w-full max-w-sm" }}
                  className={"min-w-[16rem] md:min-w-[20rem]"}
                  {...register("iban", {
                    required: t`Toto pole je povinné`,
                    validate: (value) => isValid(value) || t`IBAN není v platném formátu`,
                  })}
                  isError={!!errors.iban?.message}
                  messages={errors.iban?.message ? [{ variant: "error", text: errors.iban.message }] : undefined}
                />
              )}
            </div>

            <div className={"flex items-center justify-end gap-6"}>
              <Button
                variant={"default"}
                color={"gray"}
                size={"sm"}
                type={"button"}
                onClick={() => setSelectedProvider(undefined)}
              >
                <Trans>Zpět</Trans>
              </Button>
              <Button variant={"default"} color={"primary"} size={"md"} type={"submit"}>
                <Trans>Připojit banku</Trans>
              </Button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}
